import { message } from 'antd';
import { cloneDeep, isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { UseQueryOptions, UseQueryResult, useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { escapeInterval } from '@src/emp-3.0/utils/escape';
import {
  refetchDimensionRelated,
  refetchFlowRelated,
  refetchMappedEventRelated,
  refetchMetricRelated,
  refetchRawConfigRelated,
  refetchSensitiveConfigRelated,
} from '@src/emp-3.0/utils/refetch';
import { IActivateMappedEventPayload, IMappedEvent } from '@src/pages/metric-templates/types';
import { logger, queryClient } from '@src/routers/constants';
import commonSelector from '@store/common/selectors';

import {
  EAttributeType,
  EStringOp,
  ESystemType,
  ICreateCustomDimensionPayload,
  IEventRule,
  IFlows,
  IFunnelPayload,
  IMetric,
  IUpdateCustomDimensionPayload,
  TAttributeType,
  TSystemType,
} from '../types';
import {
  CUSTOM_DIMENSION_LIST,
  GET_EXPLORER_EVENT_LIST,
  GET_EXPLORER_IP_LIST,
  GET_EXPLORER_USERS_LIST,
  GET_MAPPED_EVENT_BY_ID,
  GET_MAPPED_EVENT_NAMES,
  GET_RAW_CONFIG,
  GET_REMAIN_COUNT,
  GET_SHARE_FUNNEL,
  GET_SHARE_SANKEY,
  GET_USER_INFO_BY_USER_ID_OR_CLIENT_ID,
  METRIC_LIST_QUERY_KEY,
  POST_ATTRIBUTE_TYPE_QUERY_KEY,
  USER_DEFINED_CUSTOM_DIMENSION,
  USE_CUSTOM_DIMENSION_MANAGEMENT_LIST,
  GET_METRIC_MANAGEMENT_LIST,
  GET_FLOW_DETAILS_BY_ID,
  GET_FLOW_LABELS,
  GET_JOURNEY_STAGES,
  METRIC_SUPPORTED_UNITS_QUERY_KEY,
  GET_FLOW_LIST,
  GET_FLOW_OUTPUTS,
  GET_FLOW_MANAGEMENT_LIST,
  GET_METRIC_DETAIL_IN_MANAGEMENT,
  GET_EVENT_LIST_FOR_METRIC,
  GET_SENSITIVE_CONFIG,
  GET_EVENT_LIST_FOR_FLOW,
  GET_SENSITIVE_CONFIG_META,
  GET_DATA_DICTIONARY,
  GET_CALCULABLE_METRICS,
} from '../utils/constants';
import {
  deleteCustomDimension,
  deploy,
  getCustomDimensionById,
  getCustomDimensionList,
  getCustomDimensionManagementList,
  getPropagationDimensions,
  getExplorerCurrentIp,
  getExplorerQuery,
  getMappedEventById,
  getMappedEventNames,
  getMetricList,
  getRawConfig,
  getRemainCount,
  getSankeyDimensions,
  getShareFunnel,
  getShareSankey,
  getUserIdByFunnel,
  getUserIdBySinglePath,
  postActivateMappedEvent,
  postAttributeTypes,
  postCustomDimension,
  postFunnel,
  postRawConfig,
  postSetWhiteList,
  postShareFunnel,
  postShareSankey,
  postShowFunnel,
  postShowSankey,
  putCustomDimension,
  getDraftMetadata,
  getMetricSupportedUnits,
  getMetricManagementList,
  postCreateFlow,
  getFlowDetailsById,
  getFlowList,
  putUpdateFlow,
  getFlowOutputs,
  getFlowLabels,
  createFlowLabel,
  updateFlowLabel,
  deleteFlowLabel,
  getJourneyStages,
  createJourneyStage,
  requestDeleteFlowById,
  getFlowsManagementList,
  getFlowsDetailInManagement,
  undeploy,
  getEventListForCreateMetric,
  postCheckDuplicateMappedEvent,
  bulkDelete,
  updateActivateMappedEvent,
  getSensitiveConfigs,
  postSensitiveConfig,
  putSensitiveConfig,
  deleteSensitiveConfig,
  getSensitiveConfigsMeta,
  getEventListForCreateFlow,
  putActivateFlow,
  postActivateFlow,
  postFlowValidatePattern,
  postGetDataDictionary,
  getValidTimeRange,
  getCalculableMetrics,
} from './queries';
import { addShareId } from '../components/Sankey/utils';
import { removeNullFromErrorEvent } from '@src/pages/workflow/utils';
import { useNewDataModel } from '@hooks/useNewDataModel';
import calcShortcutInterval, {
  calcShortcutIntervalWithinBoundary,
} from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import { trimMappedEventRules } from '@components/mapping-panel/utils/trimUtils';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { transformGroupByResToList } from '@src/emp-3.0/utils/mappedEvents/transformGroupByResToList';
import { defaultCalenderBoundry } from '../components/Sankey/constants';

export const useGetMetricList = (): UseQueryResult<IMetric[], unknown> => {
  return useQuery([METRIC_LIST_QUERY_KEY], async () => {
    const res = await getMetricList();
    return res?.data?.data;
  });
};

export const useGetMetricSupportedUnits = () => {
  return useQuery([METRIC_SUPPORTED_UNITS_QUERY_KEY], async () => {
    const res = await getMetricSupportedUnits();
    return res?.data?.data;
  });
};

export const useGetMappedEventById = (props?: any) => {
  return useQuery(
    [GET_MAPPED_EVENT_BY_ID, props?.id],
    async () => {
      const res = await getMappedEventById(props.id);
      return res.data;
    },
    {
      cacheTime: 0,
      enabled: !!props?.id,
      onError: (error: any) => {
        message.error(error?.response?.data?.msg);
      },
      ...props?.options,
    },
  );
};

export const useCreateActivateMappedEvent = () => {
  return useMutation(async (payload: IActivateMappedEventPayload) => {
    const res = await postActivateMappedEvent(payload);
    refetchMappedEventRelated();
    return res.data;
  });
};

export const useCheckDuplicateMappedEvent = () => {
  return useMutation(async (payload: IMappedEvent) => {
    const rules = trimMappedEventRules(payload.rules) as IEventRule[][];
    payload.rules = rules;
    const res = await postCheckDuplicateMappedEvent(payload);
    return res.data;
  });
};

export const useUpdateActivateMappedEvent = () => {
  return useMutation(
    async ({
      event_id,
      queryBody,
    }: {
      event_id: number;
      queryBody: IActivateMappedEventPayload;
    }) => {
      const res = await updateActivateMappedEvent(event_id, queryBody);
      refetchDimensionRelated();
      refetchMappedEventRelated();
      return res.data;
    },
  );
};

// Dimensions
export const useCustomDimensionList = () => {
  return useQuery([CUSTOM_DIMENSION_LIST], async () => {
    const res = await getCustomDimensionList();
    return res?.data?.data || [];
  });
};

export const useGetCustomDimension = (props: {
  custom_dimension_id: number;
  options?: UseQueryOptions<any, any, any, any>;
}) => {
  return useQuery(
    [USER_DEFINED_CUSTOM_DIMENSION, props],
    async () => {
      const res = await getCustomDimensionById(props?.custom_dimension_id);
      return res.data;
    },
    {
      enabled: !!props?.custom_dimension_id,
      cacheTime: 0,
      staleTime: 0,
      ...props?.options,
    },
  );
};

export const useCustomDimensionManagementList = () => {
  return useQuery([USE_CUSTOM_DIMENSION_MANAGEMENT_LIST], async () => {
    const res = await getCustomDimensionManagementList();
    return res?.data?.data || [];
  });
};

export const usePropagationDimensions = (eventIds: number[]) => {
  return useQuery(
    ['GET_PROPAGATION_DIMENSIONS', eventIds],
    async () => {
      const res = await getPropagationDimensions(eventIds);
      return res?.data?.data || [];
    },
    {
      enabled: eventIds && eventIds.length > 1 && eventIds.some((id) => id != null),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

export const useCreateCustomDimension = () => {
  return useMutation(async ({ queryBody }: { queryBody: ICreateCustomDimensionPayload }) => {
    const res = await postCustomDimension(queryBody);
    refetchDimensionRelated();
    return res.data;
  });
};

export const useUpdateCustomDimension = () => {
  return useMutation(
    async ({
      custom_dimension_id,
      queryBody,
    }: {
      custom_dimension_id: number;
      queryBody: IUpdateCustomDimensionPayload;
    }) => {
      const res = await putCustomDimension(custom_dimension_id, queryBody);
      refetchDimensionRelated();
      queryClient.invalidateQueries([USER_DEFINED_CUSTOM_DIMENSION]);
      return res?.data;
    },
  );
};

export const useDeleteCustomDimension = () => {
  return useMutation(async (custom_dimension_id: number) => {
    const res = await deleteCustomDimension(custom_dimension_id);
    refetchDimensionRelated();
    return res?.data;
  });
};

export const useDeploy = () => {
  return useMutation(
    async (props: { resource: string; payload: { deploy_all: boolean; ids: number[] } }) => {
      const res = await deploy(props.resource, props.payload);
      refetchFlowRelated();
      refetchMetricRelated();
      refetchDimensionRelated();
      refetchMappedEventRelated();
      return res?.data;
    },
  );
};

export const useUndeploy = () => {
  return useMutation(async (props: { resource: string; payload: { ids: number[] } }) => {
    const res = await undeploy(props.resource, props.payload);
    refetchFlowRelated();
    refetchMetricRelated();
    refetchDimensionRelated();
    refetchMappedEventRelated();
    return res?.data;
  });
};

export const useDelete = () => {
  return useMutation(async (props: { resource: string; payload: { ids: number[] } }) => {
    const res = await bulkDelete(props.resource, props.payload);
    Promise.all([
      refetchFlowRelated(),
      refetchMetricRelated(),
      refetchDimensionRelated(),
      refetchMappedEventRelated(),
    ]);
    return res?.data;
  });
};

export const useGetSankeyDimensions = (props?: { src: TSystemType }) => {
  return useQuery(['GET_SANKEY_DIMENSIONS', props], async () => {
    const res = await getSankeyDimensions(props?.src);
    return res?.data?.data || [];
  });
};

export const useShowSankey = () => {
  return useMutation(async (props?: { src?: TSystemType; payload: any }) => {
    const payload = addShareId(props?.payload);
    const res = await postShowSankey(props?.src, payload);
    return res?.data;
  });
};

export const useShowFunnel = () => {
  return useMutation(async (props?: { src?: TSystemType; payload: any }) => {
    const payload = addShareId(props?.payload);
    const res = await postShowFunnel(props?.src, payload);
    return res?.data;
  });
};

export const useGetUserIdBySinglePath = () => {
  return useMutation(async (props?: { src?: TSystemType; payload: any }) => {
    const payload = addShareId(props?.payload);
    const res = await getUserIdBySinglePath(props?.src, payload);
    return res?.data;
  });
};

export const useGetUserIdByFunnel = () => {
  return useMutation(async (props?: { src?: TSystemType; payload: any }) => {
    const payload = addShareId(props?.payload);
    const res = await getUserIdByFunnel(props?.src, payload);
    return res?.data;
  });
};

export const usePostFunnel = () => {
  return useMutation(
    async (props: { src?: TSystemType; is_in_sankey?: 0 | 1; payload: IFunnelPayload }) => {
      const payload = addShareId(props?.payload);
      const res = await postFunnel(props?.src, props?.is_in_sankey, payload);
      return res?.data;
    },
  );
};

export const useGetRemainCount = (props?: { options?: UseQueryOptions<any, any, any, any> }) => {
  return useQuery(
    [GET_REMAIN_COUNT, props],
    async () => {
      const res = await getRemainCount();
      return (
        res?.data?.data || {
          remain_count: 0,
          total_count: 30,
        }
      );
    },
    {
      ...props?.options,
    },
  );
};

export const useMappedEventNames = (props: {
  interval: string;
  event_tag: string;
  options?: UseQueryOptions<any, any, any, any>;
}) => {
  return useQuery(
    [GET_MAPPED_EVENT_NAMES, props],
    async () => {
      const res = await getMappedEventNames(escapeInterval(props.interval), props?.event_tag);
      return (
        res?.data?.data || {
          mapped_event_names: [],
          raw_event_names: [],
        }
      );
    },
    {
      enabled: !!props.interval && !!props.event_tag,
      ...props?.options,
    },
  );
};

export const useGetRawConfig = () => {
  return useQuery([GET_RAW_CONFIG], async () => {
    const res = await getRawConfig();
    return res?.data?.data;
  });
};

export const useUpdateRawConfig = () => {
  return useMutation(async (props: any) => {
    const res = await postRawConfig(props?.payload);
    refetchRawConfigRelated();
    return res?.data;
  });
};

export const useGetSensitiveConfigs = (filter) => {
  return useQuery([GET_SENSITIVE_CONFIG], async () => {
    const res = await getSensitiveConfigs(filter);
    return res?.data?.data;
  });
};

export const useCreateSensitiveConfigs = () => {
  return useMutation(async ({ payload }: any) => {
    const res = await postSensitiveConfig(payload);
    refetchSensitiveConfigRelated();
    return res?.data;
  });
};

export const useUpdateSensitiveConfig = () => {
  return useMutation(async ({ payload }: any) => {
    const res = await putSensitiveConfig(payload.config_id, { config: payload.config });
    refetchSensitiveConfigRelated();
    return res?.data;
  });
};

export const useDeleteSensitiveConfig = () => {
  return useMutation(async ({ payload }: any) => {
    const res = await deleteSensitiveConfig(payload.config_id);
    refetchSensitiveConfigRelated();
    return res?.data;
  });
};

export const useGetSensitiveConfigsMeta = () => {
  return useQuery([GET_SENSITIVE_CONFIG_META], async () => {
    const res = await getSensitiveConfigsMeta();
    return res?.data?.data;
  });
};

const generateFetchAttributePayload = ({
  attribute_type = EAttributeType.Global,
  interval,
  value,
  eventName,
}: {
  attribute_type: TAttributeType;
  interval: string;
  value?: string;
  eventName?: string;
}) => {
  const filters: any[] = [];
  if (value && !isEmpty(value)) {
    filters.push([
      {
        field: 'merged_event_tag_key',
        key: 'merged_event_tag_key',
        op: EStringOp.CONTAINS,
        value: value,
        display: value,
        originalField: 'merged_event_tag_key',
        _order: 0,
      },
    ]);
  }
  if (eventName) {
    filters.push([
      {
        field: 'mapped_event',
        key: 'mapped_event',
        op: EStringOp.EQ,
        value: eventName,
        display: eventName,
        originalField: 'mapped_event',
        _order: 0,
      },
    ]);
  }

  return {
    attribute_type: attribute_type,
    interval,
    ...(filters.length > 0 && { filters }),
  };
};

export const useFetchAttributeTypes = (
  props: { value?: string; interval?: any; eventName?: string; filters?: any[] } = { value: '' },
  options?: UseQueryOptions<any, any, any, any>,
) => {
  const { value, interval, eventName, filters } = props;
  const { interval: commonInterval } = useSelector(commonSelector);
  let intervalStr = `${commonInterval?.from}/${commonInterval?.to}`;
  if (interval) {
    const [startTime, endTime] = interval;
    intervalStr = `${startTime.format()}/${endTime.format()}`;
  }
  const globalAttributePayload = generateFetchAttributePayload({
    attribute_type: EAttributeType.Global,
    interval: intervalStr,
    value,
    eventName,
  });
  const eventAttributePayload = generateFetchAttributePayload({
    attribute_type: EAttributeType.Event,
    interval: intervalStr,
    value,
    eventName,
  });
  if (filters) {
    globalAttributePayload.filters = filters;
    eventAttributePayload.filters = filters;
  }
  return useQuery(
    [POST_ATTRIBUTE_TYPE_QUERY_KEY, globalAttributePayload, eventAttributePayload],
    async () => {
      const results = await Promise.allSettled([
        postAttributeTypes(globalAttributePayload),
        postAttributeTypes(eventAttributePayload),
      ]);

      const globalKeysRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const eventKeysRes = results[1].status === 'fulfilled' ? results[1].value : null;

      const allRejected = results.every((result) => result.status === 'rejected');
      if (allRejected) {
        const errors = results
          .map((result) => (result as PromiseRejectedResult)?.reason)
          .filter(Boolean);
        throw new Error(`${errors.join(', ')}`);
      }

      return {
        global_keys: globalKeysRes?.data?.data || [],
        event_keys: eventKeysRes?.data?.data || [],
      };
    },
    {
      ...options,
    },
  );
};

export const useGetShareSankey = (
  sankey_share_id: number | string,
  options?: UseQueryOptions<any, any, any, any>,
) => {
  return useQuery(
    [GET_SHARE_SANKEY, sankey_share_id],
    async () => {
      const res = await getShareSankey(sankey_share_id);
      return res?.data?.data;
    },
    {
      ...options,
    },
  );
};

export const usePostShareSankey = () => {
  return useMutation(async (props: { payload: any }) => {
    const res = await postShareSankey(props.payload);
    return res?.data?.data;
  });
};

export const useGetShareFunnel = (
  funnel_share_id: number | string,
  options?: UseQueryOptions<any, any, any, any>,
) => {
  return useQuery(
    [GET_SHARE_FUNNEL, funnel_share_id],
    async () => {
      const res = await getShareFunnel(funnel_share_id);
      return res?.data?.data;
    },
    {
      ...options,
    },
  );
};

export const usePostShareFunnel = () => {
  return useMutation(async (props: { payload: any }) => {
    const res = await postShareFunnel(props.payload);
    return res?.data?.data;
  });
};

export const useGetDraftMetadata = (props?: any) => {
  return useQuery(
    ['GET_DRAFT_METADATA'],
    async () => {
      const res = await getDraftMetadata();
      return res?.data?.data;
    },
    {
      refetchInterval: 60000,
    },
  );
};

// get explorer ip list with get current user IP and local storage
export const useGetExplorerCurrentIp = (config, options) => {
  return useQuery(
    [GET_EXPLORER_IP_LIST, config],
    async () => {
      try {
        const res = await getExplorerCurrentIp(config);
        const currentIP = res.data?.data;

        const { ip_v4, ip_v6 } = currentIP;
        return ip_v4 ? { type: 'ip_v4', value: ip_v4 } : { type: 'ip_v6', value: ip_v6 };
      } catch (e) {
        logger.info('useGetExplorerCurrentIp: ', e);
      }
    },
    options,
  );
};

const getMaxInterval = (value) => {
  // query data within a maximum of 5 minutes
  let startTime = value.split('/')?.[0] || moment(value).format();
  if (moment().diff(moment(startTime), 'minutes') > 5) {
    startTime = moment().subtract(5, 'minutes').format();
  }
  // for test
  // const startTime = moment().subtract(1, 'hours').format();
  return `${startTime}/${moment().format()}`;
};

// get explorer users list by selected IP
export const useGetExplorerUsersListByIP = (payload, params) => {
  return useQuery(
    [GET_EXPLORER_USERS_LIST, payload.filters, payload.interval],
    async () => {
      const payloadcp = cloneDeep(payload);
      payloadcp.interval = getMaxInterval(payload.interval);
      const res = await getExplorerQuery(payloadcp);
      return res?.data?.data || [];
    },
    {
      enabled: params.enabled && params.fetching,
      refetchInterval: params.interval,
      refetchOnWindowFocus: true,
    },
  );
};

// get explorer event list by selected explorer filter
export const useGetExplorerEventList = (payload, params) => {
  return useQuery(
    [GET_EXPLORER_EVENT_LIST, payload],
    async () => {
      const payloadcp = cloneDeep(payload);
      payloadcp.interval = getMaxInterval(payload.interval);
      const res = await getExplorerQuery(payloadcp);
      return res?.data?.data || null;
    },
    {
      refetchInterval: params.fetching ? params.interval : false,
      refetchOnWindowFocus: true,
      enabled: params.enabled,
    },
  );
};

export const useGetUserInfoByUserIdOrClientId = (payload, params) => {
  return useQuery(
    [GET_USER_INFO_BY_USER_ID_OR_CLIENT_ID, payload],
    async () => {
      const payloadcp = cloneDeep(payload);
      payloadcp.interval = getMaxInterval(payload.interval);
      const res = await getExplorerQuery(payloadcp);
      return res?.data?.data || [];
    },
    {
      enabled: !!payload && params.enabled,
    },
  );
};

export const usePostSetWhiteList = () => {
  return useMutation(
    async (payload: { user_id?: string[]; client_id?: string[]; ip?: string[] }) => {
      try {
        const res = await postSetWhiteList(payload);
        return res?.data;
      } catch (e) {
        logger.info('usePostSetWhiteList: ', e);
      }
    },
  );
};

// Metrics

export const useMetricManagementList = () => {
  return useQuery([GET_METRIC_MANAGEMENT_LIST], async () => {
    const res = await getMetricManagementList();
    return res?.data?.data || [];
  });
};

// Flow
export const useGetFlowList = (): UseQueryResult<IFlows[], unknown> => {
  const { workbenchMode } = useInitialInfoStore();
  return useQuery([GET_FLOW_LIST, workbenchMode], async () => {
    const res = await getFlowList();
    const flowList = res?.data?.data || [];

    // format the data to match the flow form
    flowList.forEach((flow) => {
      // set default error event
      if (!flow.definition?.events?.error_event) {
        flow.definition.events.error_event = [];
      }
    });
    return flowList;
  });
};

export const useGetFlowOutputs = () => {
  return useQuery([GET_FLOW_OUTPUTS], async () => {
    const res = await getFlowOutputs();
    return res?.data?.data || [];
  });
};

export const useGetFlowLabels = () => {
  return useQuery([GET_FLOW_LABELS], async () => {
    const res = await getFlowLabels();
    return res?.data?.data || [];
  });
};

export const useGetJourneyStages = () => {
  return useQuery([GET_JOURNEY_STAGES], async () => {
    const res = await getJourneyStages();
    return res?.data?.data || [];
  });
};

export const useCreateFlowLabel = () => {
  return useMutation(
    async (labelName: string) => {
      const res = await createFlowLabel(labelName);
      return res?.data;
    },
    {
      onSuccess: () => {
        // Refetch the labels list after creating a new label
        queryClient.invalidateQueries([GET_FLOW_LABELS]);
        message.success('Label created successfully!');
      },
      onError: (error: any) => {
        const errorMsg = error?.response?.data?.msg || error?.message || 'Failed to create label';
        message.error(errorMsg);
      },
    },
  );
};

export const useUpdateFlowLabel = () => {
  return useMutation(
    async ({ labelId, labelName }: { labelId: number; labelName: string }) => {
      const res = await updateFlowLabel(labelId, labelName);
      return res?.data;
    },
    {
      onSuccess: () => {
        // Refetch the labels list after updating a label
        queryClient.invalidateQueries([GET_FLOW_LABELS]);
        message.success('Label updated successfully!');
      },
      onError: (error: any) => {
        const errorMsg = error?.response?.data?.msg || error?.message || 'Failed to update label';
        message.error(errorMsg);
      },
    },
  );
};

export const useDeleteFlowLabel = () => {
  return useMutation(
    async (labelId: number) => {
      const res = await deleteFlowLabel(labelId);
      return res?.data;
    },
    {
      onSuccess: () => {
        // Refetch the labels list after deleting a label
        queryClient.invalidateQueries([GET_FLOW_LABELS]);
        message.success('Label deleted successfully!');
      },
      onError: (error: any) => {
        const errorMsg = error?.response?.data?.msg || error?.message || 'Failed to delete label';
        message.error(errorMsg);
      },
    },
  );
};

export const useCreateJourneyStage = () => {
  return useMutation(
    async (journeyStage: string) => {
      const res = await createJourneyStage(journeyStage);
      return res?.data;
    },
    {
      onSuccess: () => {
        // Refetch the journey stages list after creating a new stage
        queryClient.invalidateQueries([GET_JOURNEY_STAGES]);
      },
    },
  );
};

export const useCreateFlow = () => {
  return useMutation(async (payload) => {
    const cleanedValues = removeNullFromErrorEvent(payload);
    const res = await postCreateFlow(cleanedValues);
    refetchFlowRelated();
    refetchMetricRelated();
    refetchMappedEventRelated(); // can create flow when select an raw event.
    return res?.data;
  });
};

export const useUpdateFlow = () => {
  return useMutation(async ({ flow_id, payload }: { flow_id: number; payload: any }) => {
    const cleanedValues = removeNullFromErrorEvent(payload);
    const res = await putUpdateFlow(flow_id, cleanedValues);
    refetchFlowRelated();
    refetchMetricRelated();
    refetchMappedEventRelated(); // can create flow when select an raw event.
    return res?.data;
  });
};

export const useActivateFlow = () => {
  return useMutation(async (payload) => {
    const cleanedValues = removeNullFromErrorEvent(payload);
    const res = await postActivateFlow(cleanedValues);
    refetchFlowRelated();
    refetchMetricRelated();
    refetchMappedEventRelated(); // can create flow when select an raw event.
    return res?.data;
  });
};

export const usePutActivateFlow = () => {
  return useMutation(async ({ flow_id, payload }: { flow_id: number; payload: any }) => {
    const cleanedValues = removeNullFromErrorEvent(payload);
    const res = await putActivateFlow(flow_id, cleanedValues);
    refetchFlowRelated();
    refetchMetricRelated();
    refetchMappedEventRelated(); // can create flow when select an raw event.
    return res?.data;
  });
};

export const useDeleteFlowById = () => {
  return useMutation(async (flow_id: number) => {
    const res = await requestDeleteFlowById(flow_id);
    refetchFlowRelated();
    refetchMetricRelated();
    return res?.data;
  });
};

export const useGetFlowDetailsById = (flow_id?: number) => {
  return useQuery(
    [GET_FLOW_DETAILS_BY_ID, flow_id],
    async () => {
      const res = await getFlowDetailsById(flow_id);
      return res?.data?.data || {};
    },
    { staleTime: 0, cacheTime: 0, enabled: !isNaN(flow_id) },
  );
};

export const useGetFlowsManagementList = () => {
  return useQuery([GET_FLOW_MANAGEMENT_LIST], async () => {
    const res = await getFlowsManagementList();
    return res?.data?.data || [];
  });
};

export const useGetFlowsDetailInManagement = (props) => {
  return useQuery([GET_METRIC_DETAIL_IN_MANAGEMENT, props], async () => {
    const res = await getFlowsDetailInManagement(props?.params?.flow_id, props?.params?.version_id);
    return res?.data?.data || [];
  });
};

export const useEventListForCreateMetric = (payload?: { interval: string }) => {
  const { workbenchMode } = useInitialInfoStore();
  const isNewDataModel = useNewDataModel();
  const [from, to] = calcShortcutIntervalWithinBoundary(DATE_SHORTCUTS.last7Days, ESystemType.EMP);
  const defaultInterval = payload?.interval ? payload.interval : `${from.format()}/${to.format()}`;

  return useQuery(
    [GET_EVENT_LIST_FOR_METRIC, workbenchMode, defaultInterval],
    async () => {
      const res = await getEventListForCreateMetric(defaultInterval);
      return transformGroupByResToList(res?.data?.data);
    },
    {
      refetchOnWindowFocus: 'always',
    },
  );
};

export const useEventListForCreateFlow = (interval?: string) => {
  return useQuery(
    [GET_EVENT_LIST_FOR_FLOW, interval],
    async () => {
      const [from, to] = calcShortcutIntervalWithinBoundary(
        DATE_SHORTCUTS.last7Days,
        ESystemType.EMP,
      );
      const defaultInterval = interval ? interval : `${from.format()}/${to.format()}`;
      const res = await getEventListForCreateFlow(defaultInterval);
      return res.data.data || [];
    },
    {
      refetchOnWindowFocus: 'always',
    },
  );
};

export const useGetDataDictionary = (payload, key, enabled = true) => {
  return useQuery(
    ['GET_DATA_DICTIONARY', JSON.stringify(payload), key],
    async () => {
      const results = await Promise.allSettled([
        postGetDataDictionary(payload.eventTagsPayload),
        postGetDataDictionary(payload.customTagsPayload),
      ]);

      const res1 = results[0].status === 'fulfilled' ? results[0].value : null;
      const res2 = results[1].status === 'fulfilled' ? results[1].value : null;

      const eventTagsData = res1?.data?.data || [];
      const customTagsData = res2?.data?.data || [];
      const mergedData = [...eventTagsData, ...customTagsData];

      const result = mergedData
        ?.filter((item) => {
          const key = item['eventTags::keys()'] || item['customTags::keys()'];
          return key !== 'network_request_url_path';
        })
        ?.map((item) => ({
          merged_event_tag_key: item['eventTags::keys()'] || item['customTags::keys()'],
          merged_event_tag_value: item['eventTags::values()'] || item['customTags::values()'],
          app_total_user_events: item['::app_total_user_events'],
        }));
      return result || [];
    },
    {
      enabled,
      refetchOnWindowFocus: false,
    },
  );
};

export const useGetCalculableMetrics = () => {
  const { workbenchMode } = useInitialInfoStore();
  return useQuery(
    [GET_CALCULABLE_METRICS, workbenchMode],
    async () => {
      const res = await getCalculableMetrics();
      return res?.data?.data || [];
    },
    {
      staleTime: 0,
    },
  );
};

export const useValidateFlowPattern = () => {
  return useMutation({
    mutationFn: async (payload: { pattern: string }) => {
      const res = await postFlowValidatePattern(payload);
      return res?.data;
    },
  });
};
